import {
  ANALYTICS_DATA_LAYERS,
  ANALYTICS_DELTA_BEHAVIORS,
  ANALYTICS_METRIC_SEMANTICS,
  ANALYTICS_SUMMARY_BEHAVIORS,
  ANALYTICS_TIME_BEHAVIORS,
  ANALYTICS_VISUAL_TYPES,
  createAnalyticsLibraryCategory,
  createAnalyticsLibraryEntry,
  createAnalyticsSectionLibrary
} from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { ANALYTICS_FILTER_KEYS, ANALYTICS_GROUP_BY_KEYS } from '../../analyticsFilters';

const semantics = (metric, time, summary, delta = ANALYTICS_DELTA_BEHAVIORS.NONE) => ({
  metric,
  time,
  summary,
  delta
});

const SCREEN_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.SOURCES,
  ANALYTICS_FILTER_KEYS.TAGS
];

const SOURCE_COMPARISON_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.TAGS
];

const qualityAnalyticsLibrary = createAnalyticsSectionLibrary({
  section: 'quality_of_hires',
  categories: [
    createAnalyticsLibraryCategory({
      id: 'quality_hires_core',
      label: 'Quality of Hires Beta',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'onboarding_health',
          label: 'Onboarding Health',
          category: 'quality_hires_core',
          visualType: ANALYTICS_VISUAL_TYPES.PROGRESS,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE,
          defaultSize: 'large',
          defaultVariant: 'data-forward',
          dataLayer: ANALYTICS_DATA_LAYERS.NATIVE,
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COMPOSITION,
            ANALYTICS_TIME_BEHAVIORS.COHORT_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.SHARE_OF_TOTAL
          ),
          supportedFilters: [
            ANALYTICS_FILTER_KEYS.PERIOD,
            ANALYTICS_FILTER_KEYS.DEPARTMENTS,
            ANALYTICS_FILTER_KEYS.RECRUITERS,
            ANALYTICS_FILTER_KEYS.SOURCES,
            ANALYTICS_FILTER_KEYS.TAGS
          ],
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: [
            ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
            ANALYTICS_GROUP_BY_KEYS.RECRUITER,
            ANALYTICS_GROUP_BY_KEYS.SOURCE,
            ANALYTICS_GROUP_BY_KEYS.TAG
          ],
          dataShape: {
            dimensions: ['cohort'],
            measures: ['complete', 'partial', 'incomplete', 'days_to_ready']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'early_quality_index',
          label: 'Early Quality Index Beta',
          category: 'quality_hires_core',
          visualType: ANALYTICS_VISUAL_TYPES.RADAR,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.RADAR],
          defaultGraph: ANALYTICS_GRAPH_TYPES.RADAR,
          defaultSize: 'medium',
          defaultVariant: 'contextual',
          dataLayer: ANALYTICS_DATA_LAYERS.NATIVE,
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE
          ),
          supportedFilters: [
            ANALYTICS_FILTER_KEYS.PERIOD,
            ANALYTICS_FILTER_KEYS.DEPARTMENTS,
            ANALYTICS_FILTER_KEYS.RECRUITERS,
            ANALYTICS_FILTER_KEYS.SOURCES,
            ANALYTICS_FILTER_KEYS.TAGS
          ],
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.SOURCE,
          groupByOptions: [
            ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
            ANALYTICS_GROUP_BY_KEYS.RECRUITER,
            ANALYTICS_GROUP_BY_KEYS.SOURCE,
            ANALYTICS_GROUP_BY_KEYS.TAG
          ],
          dataShape: {
            dimensions: ['entity', 'quality_axis'],
            measures: ['quality_score']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'retention_timeline',
          label: 'Retention Timeline Beta',
          category: 'quality_hires_core',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.RETENTION_HEATMAP],
          defaultGraph: ANALYTICS_GRAPH_TYPES.RETENTION_HEATMAP,
          defaultSize: 'medium',
          defaultVariant: 'data-forward',
          dataLayer: ANALYTICS_DATA_LAYERS.HYBRID,
          requiredProducts: [],
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.COHORT_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE
          ),
          supportedFilters: SOURCE_COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['cohort', 'milestone'],
            measures: ['retention_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'source_quality',
          label: 'Source Quality Beta',
          category: 'quality_hires_core',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART],
          defaultGraph: ANALYTICS_GRAPH_TYPES.BUBBLE_CHART,
          defaultSize: 'large',
          defaultVariant: 'contextual',
          dataLayer: ANALYTICS_DATA_LAYERS.HYBRID,
          requiredProducts: ['employee_outcomes'],
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.BUBBLE_CHART]: {
              presentation: 'quadrant_scatter',
              distinctCategoryTones: true,
              xMetricId: 'applicantsPerHire',
              yMetricId: 'qualityRate',
              bubbleMetricId: 'hireVolume',
              quadrantLabels: {
                topLeft: 'Efficient · high quality',
                topRight: 'High volume · high quality',
                bottomLeft: 'Efficient but weak',
                bottomRight: 'High volume and weak'
              }
            }
          },
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['source'],
            measures: ['applicants_per_hire', 'quality_rate', 'hire_volume']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'probation_performance',
          label: 'Probation & Performance Beta',
          category: 'quality_hires_core',
          visualType: ANALYTICS_VISUAL_TYPES.PROGRESS,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.STACKED_BAR_LINE,
          defaultSize: 'large',
          defaultVariant: 'data-forward',
          dataLayer: ANALYTICS_DATA_LAYERS.HYBRID,
          requiredProducts: [],
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COMPOSITION,
            ANALYTICS_TIME_BEHAVIORS.COHORT_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.SHARE_OF_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: [
            ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
            ANALYTICS_GROUP_BY_KEYS.RECRUITER,
            ANALYTICS_GROUP_BY_KEYS.TAG,
            ANALYTICS_GROUP_BY_KEYS.SOURCE
          ],
          dataShape: {
            dimensions: ['department', 'probation_outcome'],
            measures: ['passed', 'extended', 'failed', 'left_early', 'time_to_productivity']
          }
        })
      ]
    })
  ]
});

export default qualityAnalyticsLibrary;
